export function getPaymentMethodSortOrder(method) {
  const rawSortOrder = method?.sort_order
  if (rawSortOrder === null || rawSortOrder === undefined || rawSortOrder === '') {
    return 9999
  }
  const sortOrder = Number(rawSortOrder)
  return Number.isFinite(sortOrder) ? sortOrder : 9999
}

export function sortPaymentMethods(methods) {
  return methods
    .map((method, index) => ({ method, index }))
    .sort((a, b) => {
      const orderDiff = getPaymentMethodSortOrder(a.method) - getPaymentMethodSortOrder(b.method)
      return orderDiff || a.index - b.index
    })
    .map((item) => item.method)
}

export function getCurrencyBlockedPaymentCodes(paymentCurrency, blockPaymentCode = []) {
  const blockedCodes = new Set(Array.isArray(blockPaymentCode) ? blockPaymentCode : [])
  const currency = String(paymentCurrency || '').trim().toUpperCase()
  if (currency && currency !== 'USD' && currency !== 'CAD') {
    blockedCodes.add('eselectplus_api')
  }
  return Array.from(blockedCodes)
}

export function resolveCheckoutPaymentCurrency(queryData = {}, memberPayment = {}) {
  return String(memberPayment?.currency || queryData?.payment_currency || '').trim().toUpperCase()
}

// Insurance is charged to the card by the insurer, so only raw-card gateways qualify.
export const INSURANCE_PAYMENT_CODES = ['eselectplus_api', 'pp_pro']

export function buildAvailablePaymentMethods(paymentMethods, {
  isInsurancePayment = false,
  blockPaymentCode = [],
  customerCenterBlockEselectplusApi = 1,
  paymentProductType = 0
} = {}) {
  const productType = Number(paymentProductType || 0)
  const shouldBlockEselectplus =
    !isInsurancePayment &&
    productType !== 2 &&
    productType !== 3 &&
    Number(customerCenterBlockEselectplusApi ?? 1) === 1
  const blockedCodes = new Set(Array.isArray(blockPaymentCode) ? blockPaymentCode : [])
  if (shouldBlockEselectplus) {
    blockedCodes.add('eselectplus_api')
  }

  const methods = []
  for (const pm of Object.values(paymentMethods || {})) {
    if (isInsurancePayment && !INSURANCE_PAYMENT_CODES.includes(pm.code)) {
      continue
    }
    if (!blockedCodes.has(pm.code)) {
      methods.push(pm)
    }
  }

  return sortPaymentMethods(methods)
}

export function canUseSplitCreditPayment({
  paymentCreditPrice = 0,
  coinsHidden = false,
  selectedPaymentTypeAmount = 0,
  isInsurancePayment = false
} = {}) {
  return (
    !isInsurancePayment &&
    Number(paymentCreditPrice || 0) > 0 &&
    !coinsHidden &&
    Number(selectedPaymentTypeAmount || 0) > 0
  )
}

export function getPaymentCreditBalance(credits) {
  return Object.values(credits || {}).reduce((total, credit) => {
    const amount = Number(credit?.amount || 0)
    return Number.isFinite(amount) && amount > 0 ? total + amount : total
  }, 0)
}

export function getSelectedCreditIds(credits) {
  return Array.isArray(credits) ? credits.filter(Boolean) : []
}

export function calculateRemainingPaymentAmount(totalAmount, appliedCredit = 0) {
  const totalCents = Math.round(Number(totalAmount || 0) * 100)
  const appliedCents = Math.round(Number(appliedCredit || 0) * 100)

  return Math.max(0, totalCents - appliedCents) / 100
}

export function buildPaypalCheckoutCreateParams({
  orderId,
  paymentId,
  currency,
  amount,
  local = {}
}) {
  return {
    order_id: orderId,
    payment_id: paymentId,
    currency,
    amount,
    credit: local.credit || '',
    payment_amount: local.payment_amount || '',
    coin_currency: local.coin?.currency || currency,
    coin_amount: local.coin?.amount || 0
  }
}

export function classifyPaypalFailure(result) {
  const rawMessage = String(result?.msg || result?.error || '').toLowerCase()

  if (rawMessage.includes('reservation_unavailable') || rawMessage.includes('departure date is no longer available')) {
    return 'reservation_unavailable'
  }

  if (rawMessage.includes('error_cart') || rawMessage.includes('cart is no longer valid')) {
    return 'error_cart'
  }

  return 'paypal_error'
}
